import type { AuditLogDayCount, AuditLogEntry } from "src/api/client-api/auditLog/auditLogSchemas";

export const isServiceActor = (entry: AuditLogEntry): boolean => entry.actorType === "service";

export const auditLogSubject = (entry: AuditLogEntry): string | null =>
    entry.entityLabel ?? (entry.entityId === "" ? null : entry.entityId);

export const auditLogRecipient = (entry: AuditLogEntry): string | null => entry.recipientLabel;

export const auditLogScopeLabel = (entry: AuditLogEntry): string | null => entry.rootLabel ?? entry.rootId;

export type AuditLogChange = {
    field: string;
    before: unknown;
    after: unknown;
    isDiff: boolean;
};

const isDiffShape = (value: unknown): value is { before: unknown; after: unknown } =>
    typeof value === "object" && value !== null && !Array.isArray(value) && "before" in value && "after" in value;

const isEmptyState = (change: AuditLogChange): boolean =>
    change.isDiff === false && (change.after === null || change.after === undefined || change.after === "");

export const normalizeAuditLogChanges = (changes: Record<string, unknown> | null): AuditLogChange[] => {
    if (changes === null) {
        return [];
    }

    return Object.entries(changes)
        .map(([field, value]) => {
            if (isDiffShape(value)) {
                return { field, before: value.before, after: value.after, isDiff: true };
            }

            return { field, before: null, after: value, isDiff: false };
        })
        .filter((change) => isEmptyState(change) === false);
};

const ISO_DATE_TIME = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

export const isIsoDateTime = (value: string): boolean => ISO_DATE_TIME.test(value);

export const formatAuditLogValue = (value: unknown, fallbackLabel: string | null = null): string => {
    if (value === null || value === undefined) {
        return "—";
    }

    if (typeof value === "string") {
        return value === "" ? "—" : value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }

    if (typeof value === "object" && "id" in value) {
        if ("label" in value && typeof value.label === "string" && value.label !== "") {
            return value.label;
        }

        return fallbackLabel ?? String(value.id);
    }

    return JSON.stringify(value);
};

export const humanizeAuditLogField = (field: string): string => {
    const words = field
        .replace(/[_-]+/g, " ")
        .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .trim();

    return words === "" ? field : words.charAt(0).toUpperCase() + words.slice(1);
};

export const AUDIT_LOG_PERIODS = ["day", "week", "month", "all", "custom"] as const;

export type AuditLogPeriod = (typeof AUDIT_LOG_PERIODS)[number];

const PERIOD_DAYS: Record<"day" | "week" | "month", number> = {
    day: 1,
    week: 7,
    month: 30,
};

export type AuditLogCustomWindow = {
    from: string | null;
    to: string | null;
};

export type AuditLogWindow = {
    from: string | undefined;
    to: string | undefined;
};

export const emptyAuditLogCustomWindow: AuditLogCustomWindow = { from: null, to: null };

/**
 * The window bounds are typed as wall-clock times and read back as UTC, so the two have to
 * be converted explicitly. Left to a date library's default, a bound typed as 12:00 becomes
 * 10:00 for a reader in +02:00 -- the screen would then answer a question nobody asked.
 */
export const instantToUtcWallClock = (instant: string): string => {
    const moment = new Date(instant);

    return Number.isNaN(moment.getTime()) ? "" : moment.toISOString().slice(0, 19);
};

export const utcWallClockToInstant = (wallClock: string): string | null => {
    const moment = new Date(`${wallClock}Z`);

    return Number.isNaN(moment.getTime()) ? null : moment.toISOString();
};

export const isInvertedAuditLogWindow = (custom: AuditLogCustomWindow): boolean => {
    if (custom.from === null || custom.to === null) {
        return false;
    }

    const from = new Date(custom.from).getTime();
    const to = new Date(custom.to).getTime();

    return Number.isNaN(from) === false && Number.isNaN(to) === false && from > to;
};

const boundary = (value: string | null): string | undefined => {
    if (value === null || value.trim() === "") {
        return undefined;
    }

    const moment = new Date(value);

    return Number.isNaN(moment.getTime()) ? undefined : moment.toISOString();
};

export const resolveAuditLogWindow = (
    period: AuditLogPeriod,
    now: Date,
    custom: AuditLogCustomWindow = emptyAuditLogCustomWindow,
): AuditLogWindow => {
    if (period === "custom") {
        const from = boundary(custom.from);
        const to = boundary(custom.to);

        if (from !== undefined && to !== undefined && from > to) {
            return { from: to, to: from };
        }

        return { from, to };
    }

    if (period === "all") {
        return { from: undefined, to: undefined };
    }

    return {
        from: new Date(now.getTime() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000).toISOString(),
        to: undefined,
    };
};

export type AuditLogTimeLabel =
    | { kind: "seconds" }
    | { kind: "minutes"; value: number }
    | { kind: "hours"; value: number }
    | { kind: "days"; value: number };

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const auditLogTimeLabel = (occurredAt: string, now: Date): AuditLogTimeLabel => {
    const elapsed = now.getTime() - new Date(occurredAt).getTime();

    if (elapsed < MINUTE) {
        return { kind: "seconds" };
    }

    if (elapsed < HOUR) {
        return { kind: "minutes", value: Math.floor(elapsed / MINUTE) };
    }

    if (elapsed < DAY) {
        return { kind: "hours", value: Math.floor(elapsed / HOUR) };
    }

    return { kind: "days", value: Math.floor(elapsed / DAY) };
};

export const isWithinLastDay = (occurredAt: string, now: Date): boolean =>
    now.getTime() - new Date(occurredAt).getTime() < DAY;

export const auditLogTotalInWindow = (perDay: AuditLogDayCount[] | undefined): number | undefined =>
    perDay === undefined ? undefined : perDay.reduce((total, day) => total + day.count, 0);

export const auditLogDisplayTotal = (
    window: AuditLogWindow,
    perDay: AuditLogDayCount[] | undefined,
): number | undefined => (window.from === undefined ? undefined : auditLogTotalInWindow(perDay));
